<template>
  <div class="tw:w-screen tw:h-screen tw:flex tw:flex-col">
    <NPSResearch v-if="showLinkResearchNPS" :link="getLinkResearchNPS" :is-mobile="isMobile" />
  
    <section id="Layout" class="tw:w-screen tw:h-screen layout">
      <Sidebar :collapsible="collapsable" :visible="sidebar.visible" />

      <div class="layout-page">
        <MobileAppPromotion />

        <div id="topbar" class="topbar">
          <div class="topbar__tools">
            <div v-if="collapsable" class="topbar__tools__colapsable-menu">
              <a  class="toggle-sidebar" @click="layoutStore.actionToggleSidebar">
                <IconGoogle name="menu" />
              </a>
            </div>

            <div
              v-if="collapsable" class="topbar__tools__logo" :class="{
                'logo-mobile': layout.breakpoint == 1,
                'logo-tablet':
                  layout.breakpoint === 2 ||
                  (layout.breakpoint > 2 &&
                    sidebar.visible === false),
              }">
              <router-link id="home" to="/home">
                <template v-if="layout.breakpoint >= 2 && sidebar.visible === false">
                  <img v-if="logo" loading="lazy" class="lazyload" :data-src="logo" alt="logo" />
                </template>
                <template v-if="layout.breakpoint === 1">
                  <img v-if="logomarca" loading="lazy" class="lazyload" :data-src="logomarca" alt="logo" />
                </template>
              </router-link>
            </div>

            <div v-if="layout.breakpoint >= 2" class="topbar__tools__search">
              <Search />
            </div>

            <div v-if="layout.breakpoint >= 3" class="topbar__tools__promoting">
              <Promoting
                v-if="promotionApp.active" campaign="app" :app-store-link="promotionApp.appStoreLink"
                :google-play-link="promotionApp.googlePlayLink" :text="$t('topbar.promoting.app.default_text')" />
            </div>

            <div class="topbar__tools__actions" :class="{ 'no-promotion': !promotionApp.active }">
              <TopbarUser :is-mobile="isMobile" @toggle-search="state.searching = !state.searching" />
            </div>
          </div>
        </div>
        <div v-if="layout.breakpoint < 3 && promotionApp.active" class="mobile-promotion-messaage">
          <Promoting
            v-if="promotionApp.active" campaign="app" :text="$t('topbar.promoting.app.default_text')"
            :app-store-link="promotionApp.appStoreLink" :google-play-link="promotionApp.googlePlayLink" 
          />
        </div>
        <Search v-if="state.searching" class="mobile-search" :is-mobile="true" @clear="state.searching = false" />
        <div class="layout-page__overflow tw:flex-1" style="background-color: var(--layout-background-color)">
          <div
            :class="{
              'site-content tw:flex tw:flex-col tw:flex-1': true,
              'site-content--sidebar': sidebar.visible,
              'site-content--no-sidebar': sidebar.visible,
              'site-content--mobile':
                sidebar.visible && layout.breakpoint === 1,
              'tw:pt-16 tw:pb-16 tw:px-9': !props.noPadding,
              [contentClass]: true,
            }">
            <div v-if="!props.noPadding" class="tw:max-w-[1440px] tw:mx-auto tw:w-full tw:flex tw:flex-col tw:flex-1">
              <slot></slot>
            </div>
            <template v-else>
              <slot></slot>
            </template>
          </div>
          <div class="site-content--footer">
            <ScrollToTop />
            <Whatsapp v-if="whatsapp" :phone="whatsappPhone" :message="whatsappMessage" />
          </div>
        </div>
        <slot name="footer"></slot>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import NPSResearch from "./NPSResearch.vue";
import Promoting from "./PromotingBanner.vue";
import Search from "@/components/common/SearchBar/SearchBar.vue";
import Sidebar from "./SidebarNav.vue";
import TopbarUser from "./TopbarUser.vue";
import { isWithinInterval } from "date-fns";
import ScrollToTop from "@/components/common/ScrollToTopPlay/ScrollToTopPlay.vue"
import Whatsapp from "@/components/common/WhatsappWidgetPlay/WhatsappWidgetPlay.vue"
import MobileAppPromotion from "./MobileAppPromotion.vue";
import emitter from "@/events/event-bus";
import { useStateStore } from "@/store/state.store";
import { reactive, computed } from "vue";
import { onUnmounted } from "vue";
import { onMounted } from "vue";
import { onBeforeMount } from "vue";
import MemberMetaService from "@/services/resources/MemberMetaService";
import { i18n } from "@/i18n";
import { useLayoutStore } from "@/store/layout.store";
import { useConfigStore } from "@/store/config.store";
import { useUserStore } from "@/store/user.store";

import { useHomeLegacyStore } from "@/store/home-legacy.store";

const serviceMemberMeta = MemberMetaService.build();

const LOCALE_MAP: Record<string, string> = {
  BR: "pt_BR",
  US: "en",
  ES: "es",
};

const stateStore = useStateStore()
const layoutStore = useLayoutStore()
const configStore = useConfigStore()
const _userStore = useUserStore()

const props = defineProps<{
  contentClass?: string
  collapsable?: boolean
  noPadding?: boolean
}>()

const state = reactive({
  modalContent: {
    cover: {},
    title: null,
    description: null,
    button: null,
    url: null,
  },
  client: {
    width: 0,
  },
  searching: false,
})

const layout = computed(() => layoutStore.getLayout)
const sidebar = computed(() => layoutStore.getSidebar)
const getSettings = computed(() => configStore.getSettings)
const getLinkResearchNPS = computed(() => configStore.getLinkResearchNPS)
const getPromotionData = computed(() => layoutStore.getPromotionData)

const { getSetting } = stateStore


const logo = computed(() => {
  return stateStore.site?.logo
})

const logomarca = computed(() => {
  return stateStore.site?.logomarca
})

const promotionApp = computed(() => {
  let active = getPromotionData.value?.active || false;

  if (active) {
    const period = getPromotionData.value?.period || null;

    if (period) {
      try {
        const now = new Date();
        const startDate = new Date(period.startDate);
        const endDate = new Date(period.endDate);

        active =
          isWithinInterval(now, { start: startDate, end: endDate }) &&
          stateStore.role == "student";
      } catch (_error) {
        console.error(
          "Invalid date format in promotion period"
        );
        active = false;
      }
    }
  }

  return {
    active: active,
    appStoreLink: getPromotionData.value?.appStoreLink || "",
    googlePlayLink: getPromotionData.value?.googlePlayLink || "",
  };
})

const isMobile = computed(() => {
  return state.client.width <= 576;
})

const whatsapp = computed(() => {
  const whatsapp = getSetting("whatsapp");
  const number = getSetting("whatsapp_number");

  const messageCheck = whatsapp && typeof number === "string" && number.length > 0;
  return messageCheck;
})

const whatsappPhone = computed(() => getSetting("whatsapp_number") || '')
const whatsappMessage = computed(() => getSetting("whatsapp_message") || '')

const showLinkResearchNPS = computed(() => {
  const hasLink = !!getLinkResearchNPS.value;
  const role = stateStore.role;
  const isStudent = role == "student";
  
  return hasLink && isStudent;
})


const homeLegacyStore = useHomeLegacyStore()

const _actions = {
  fetchHomeData: (payload?: unknown) => homeLegacyStore.actionFetchHomeData(payload as { page?: number }),
  showModalInfoCourse: (payload?: unknown) => homeLegacyStore.actionShowModalInfoCourse(payload as unknown),
  showModalBuyCourse: (payload?: unknown) => homeLegacyStore.actionShowModalBuyCourse(payload as unknown),
}

function detectOs() {
  const userAgent =
    navigator.userAgent || navigator.vendor || window['opera'];
  if (/android/i.test(userAgent)) {
    return "Android";
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window['MSStream']) {
    return "iOS";
  }
  return "Desktop";
}

function initWhatsApp() {
  const enabled = getSetting("whatsapp");
  const number = getSetting("whatsapp_number");
  const message = getSetting("whatsapp_message");

  if (!enabled && !number && !message) {
    return;
  }
}

function initJivoChat() {
  const enabled = getSettings.value["jivochat"];
  const id = getSettings.value["id_jivochat"];
  if (!enabled || !id) {
    return;
  }
  const jivoScript = document.createElement("script");
  jivoScript.setAttribute("src", `//code.jivosite.com/widget/${id}`);
  jivoScript.setAttribute("id", "jivoscript");
  jivoScript.async = true;
  document.head.appendChild(jivoScript);

  jivoScript.onload = () => {
    adjustElementsPosition();
  };
}

function adjustElementsPosition() {
  setTimeout(() => {
    const jvlabelWrap = document.getElementById("jvlabelWrap");

    if (jvlabelWrap) {
      jvlabelWrap.style.right = "70px";
    }
    observeJivoChat();
  }, 2000);
}

function observeJivoChat() {
  const target = document.getElementById("jcont");
  if (target) {
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === "attributes") {
          setTimeout(() => {
            const jvlabelWrap =
              document.getElementById("jvlabelWrap");
            if (jvlabelWrap) {
              jvlabelWrap.style.right = "70px";
            }
          }, 100);
        }
      });
    });

    const config = { attributes: true };
    observer.observe(target, config);
  } else {
    requestAnimationFrame(observeJivoChat);
  }
}

function handleResize() {
  state.client.width = window.innerWidth;
}


onMounted(async () => {
  await delayLoaded(() => {
    return layoutStore.actionFetchPromotionStatus().then(() => {
      emitter.$emit("promotionStatusFetched");
    });
  });
})

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
})

async function initLocale() {
  try {
    const resp = await serviceMemberMeta.search("keys[]=pais");
    const code = resp?.pais;
    if (code && LOCALE_MAP[code]) {
      (i18n.global.locale as unknown as { value: string }).value = LOCALE_MAP[code];
    }
  } catch {
    // mantém o locale padrão pt_BR
  }
}

onBeforeMount(async () => {
  configStore.actionSetIsLoading(true);
  window.addEventListener("resize", handleResize);
  handleResize();

  configStore.actionDefineMainColor();
  initLocale();

  try {
    if (detectOs() === "Android") {
      window.screen.orientation.lock("portrait");
    }
  } catch (error) {
    console.error("An error occurred:", error);
  } finally {
    configStore.actionSetIsLoading(false);
  }
  initWhatsApp();
  initJivoChat();
})

</script>

<style lang="scss" scoped>
#home {
  .logo-mobile {
    min-height: 40px;
    flex-basis: 116px;
    align-items: center;
    display: flex;

    img {
      min-width: 116px;
      max-width: 116px;
      display: block;
    }
  }

  .logo-tablet {
    min-height: 40px;
    flex-basis: 38px;
    align-items: center;
    display: flex;

    img {
      min-width: 38px;
      max-width: 38px;
      display: block;
    }
  }
}

.layout {
  display: flex;
  flex-direction: row;
  overflow: hidden;
}

.layout-page {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 0;

  &__overflow {
    flex: 1;
    // display: flex;
    // width: 100%;

    overflow: auto;
    overscroll-behavior: smooth;
  }
}

.mobile-promotion-messaage {
  display: none;
}

[data-device="smartphone"],
[data-device="tablet"] {
  .mobile-promotion-messaage {
    display: block;
    width: 100%;
    padding: 1rem;
  }
}

.topbar {
  position: relative;
  background-color: var(--topbar-background);
  border-bottom: 1px solid var(--topbar-border);
  padding: 0.625rem 2.25rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 4rem;

  &__tools {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 1rem;

    &__logo {
      &.logo-mobile {
        min-height: 40px;
        flex-basis: 116px;
        align-items: center;
        display: flex;

        img {
          min-width: 116px;
          max-width: 116px;
          max-height: 4rem;
          object-fit: contain;
          display: block;
        }
      }

      &.logo-tablet {
        min-height: 40px;
        flex-basis: 38px;
        align-items: center;
        display: flex;

        img {
          min-width: 38px;
          max-width: 38px;
          display: block;
        }
      }
    }

    &__search {
      flex: 1;
      max-width: 500px;
    }

    &__promoting {
      width: fit-content;
    }

    &__actions {
      margin-left: auto;
    }
  }

  @media screen and (max-width: 600px) {
    justify-content: space-between;
    padding: 0.625rem 1rem;
    gap: 0;
  }
}

.toggle-sidebar {
  cursor: pointer;

  :deep(svg path ) {
    fill: var(--opposite-color);
  }
}

.mobile-search {
  padding: 1rem;
  flex-direction: row;
  width: 100%;
  height: 4.6rem;
}

.flex-1 {
  display: flex;
  align-items: center;
}
</style>
